/*
|--------------------------------------------------------------------------
| PURCHASE REPORT CSV EXPORT
|--------------------------------------------------------------------------
*/


/*
|--------------------------------------------------------------------------
| BASIC HELPERS
|--------------------------------------------------------------------------
*/

const toNumber = (
    value,
    fallback = 0
) => {
    const number =
        Number(value);

    return Number.isFinite(
        number
    )
        ? number
        : fallback;
};


const formatNumber = (
    value
) =>
    toNumber(
        value
    ).toFixed(
        2
    );


const formatDate = (
    value
) => {
    if (
        !value
    ) {
        return "";
    }

    const date =
        new Date(
            value
        );

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "";
    }

    return new Intl.DateTimeFormat(
        "en-IN",
        {
            day:
                "2-digit",

            month:
                "2-digit",

            year:
                "numeric",
        }
    ).format(
        date
    );
};


const formatText = (
    value
) =>
    String(
        value ??
        ""
    )
        .replace(
            /_/g,
            " "
        )
        .trim();


/*
|--------------------------------------------------------------------------
| CSV ESCAPE
|--------------------------------------------------------------------------
*/

const escapeCsvValue = (
    value
) => {
    const text =
        String(
            value ??
            ""
        );

    if (
        text.includes(
            ","
        ) ||
        text.includes(
            "\""
        ) ||
        text.includes(
            "\n"
        ) ||
        text.includes(
            "\r"
        )
    ) {
        return `"${text.replace(
            /"/g,
            "\"\""
        )}"`;
    }

    return text;
};


/*
|--------------------------------------------------------------------------
| GENERATE CSV
|--------------------------------------------------------------------------
*/

const generateCsv = (
    columns,
    records
) => {
    const header =
        columns
            .map(
                (
                    column
                ) =>
                    escapeCsvValue(
                        column.label
                    )
            )
            .join(
                ","
            );

    const rows =
        records.map(
            (
                record
            ) =>
                columns
                    .map(
                        (
                            column
                        ) => {
                            const value =
                                typeof column.value ===
                                    "function"
                                    ? column.value(
                                        record
                                    )
                                    : record?.[
                                    column
                                        .value
                                    ];

                            return escapeCsvValue(
                                value
                            );
                        }
                    )
                    .join(
                        ","
                    )
        );

    return [
        header,
        ...rows,
    ].join(
        "\n"
    );
};


/*
|--------------------------------------------------------------------------
| DOWNLOAD CSV
|--------------------------------------------------------------------------
*/

const downloadCsv = (
    filename,
    csvContent
) => {
    /*
    |--------------------------------------------------------------------------
    | BOM helps Excel correctly display UTF-8.
    |--------------------------------------------------------------------------
    */

    const content =
        `\uFEFF${csvContent}`;

    const blob =
        new Blob(
            [
                content,
            ],
            {
                type:
                    "text/csv;charset=utf-8;",
            }
        );

    const url =
        URL.createObjectURL(
            blob
        );

    const link =
        document.createElement(
            "a"
        );

    link.href =
        url;

    link.setAttribute(
        "download",
        filename
    );

    document.body.appendChild(
        link
    );

    link.click();

    document.body.removeChild(
        link
    );

    URL.revokeObjectURL(
        url
    );
};


/*
|--------------------------------------------------------------------------
| PURCHASE REGISTER
|--------------------------------------------------------------------------
*/

const purchaseRegisterColumns = [
    {
        label:
            "Purchase Bill",

        value: (
            item
        ) =>
            item
                ?.documentNumber ||
            item
                ?.purchaseBillNumber ||
            "",
    },

    {
        label:
            "Bill Date",

        value: (
            item
        ) =>
            formatDate(
                item
                    ?.documentDate
            ),
    },

    {
        label:
            "Supplier",

        value: (
            item
        ) =>
            item
                ?.supplier
                ?.supplierName ||
            item
                ?.supplier
                ?.companyName ||
            "",
    },

    {
        label:
            "Supplier GSTIN",

        value: (
            item
        ) =>
            item
                ?.supplier
                ?.gstin ||
            "",
    },

    {
        label:
            "Supplier Invoice",

        value: (
            item
        ) =>
            item
                ?.supplierInvoiceNumber ||
            "",
    },

    {
        label:
            "Supplier Invoice Date",

        value: (
            item
        ) =>
            formatDate(
                item
                    ?.supplierInvoiceDate
            ),
    },

    {
        label:
            "Taxable Amount",

        value: (
            item
        ) =>
            formatNumber(
                item
                    ?.taxableAmount
            ),
    },

    {
        label:
            "CGST",

        value: (
            item
        ) =>
            formatNumber(
                item
                    ?.cgstAmount
            ),
    },

    {
        label:
            "SGST",

        value: (
            item
        ) =>
            formatNumber(
                item
                    ?.sgstAmount
            ),
    },

    {
        label:
            "IGST",

        value: (
            item
        ) =>
            formatNumber(
                item
                    ?.igstAmount
            ),
    },

    {
        label:
            "Total Tax",

        value: (
            item
        ) =>
            formatNumber(
                item
                    ?.taxAmount
            ),
    },

    {
        label:
            "Grand Total",

        value: (
            item
        ) =>
            formatNumber(
                item
                    ?.grandTotal
            ),
    },

    {
        label:
            "Debit Notes",

        value: (
            item
        ) =>
            formatNumber(
                item
                    ?.debitNoteAmount
            ),
    },

    {
        label:
            "Paid",

        value: (
            item
        ) =>
            formatNumber(
                item
                    ?.paidAmount
            ),
    },

    {
        label:
            "Outstanding",

        value: (
            item
        ) =>
            formatNumber(
                item
                    ?.outstandingAmount
            ),
    },

    {
        label:
            "Status",

        value: (
            item
        ) =>
            formatText(
                item
                    ?.documentStatus
            ),
    },

    {
        label:
            "Payment Status",

        value: (
            item
        ) =>
            formatText(
                item
                    ?.paymentStatus
            ),
    },
];


/*
|--------------------------------------------------------------------------
| SUPPLIER SUMMARY
|--------------------------------------------------------------------------
*/

const supplierSummaryColumns = [
    {
        label:
            "Supplier",

        value: (
            item
        ) =>
            item
                ?.supplier
                ?.supplierName ||
            item
                ?.supplier
                ?.companyName ||
            "",
    },

    {
        label:
            "Supplier Code",

        value: (
            item
        ) =>
            item
                ?.supplier
                ?.supplierCode ||
            "",
    },

    {
        label:
            "GSTIN",

        value: (
            item
        ) =>
            item
                ?.supplier
                ?.gstin ||
            "",
    },

    {
        label:
            "Mobile",

        value: (
            item
        ) =>
            item
                ?.supplier
                ?.mobile ||
            "",
    },

    {
        label:
            "Bills",

        value: (
            item
        ) =>
            toNumber(
                item
                    ?.totalBills
            ),
    },

    {
        label:
            "Taxable",

        value: (
            item
        ) =>
            formatNumber(
                item
                    ?.taxableAmount
            ),
    },

    {
        label:
            "Tax",

        value: (
            item
        ) =>
            formatNumber(
                item
                    ?.taxAmount
            ),
    },

    {
        label:
            "Purchase",

        value: (
            item
        ) =>
            formatNumber(
                item
                    ?.grandTotal
            ),
    },

    {
        label:
            "Debit Notes",

        value: (
            item
        ) =>
            formatNumber(
                item
                    ?.debitNoteAmount
            ),
    },

    {
        label:
            "Paid",

        value: (
            item
        ) =>
            formatNumber(
                item
                    ?.paidAmount
            ),
    },

    {
        label:
            "Outstanding",

        value: (
            item
        ) =>
            formatNumber(
                item
                    ?.outstandingAmount
            ),
    },

    {
        label:
            "Paid Bills",

        value: (
            item
        ) =>
            toNumber(
                item
                    ?.paidBills
            ),
    },

    {
        label:
            "Partial Bills",

        value: (
            item
        ) =>
            toNumber(
                item
                    ?.partiallyPaidBills
            ),
    },

    {
        label:
            "Unpaid Bills",

        value: (
            item
        ) =>
            toNumber(
                item
                    ?.unpaidBills
            ),
    },
];


/*
|--------------------------------------------------------------------------
| DEBIT NOTE
|--------------------------------------------------------------------------
*/

const debitNoteColumns = [
    {
        label:
            "Debit Note",

        value: (
            item
        ) =>
            item
                ?.debitNoteNumber ||
            "",
    },

    {
        label:
            "Date",

        value: (
            item
        ) =>
            formatDate(
                item
                    ?.debitNoteDate
            ),
    },

    {
        label:
            "Purchase Bill",

        value: (
            item
        ) =>
            item
                ?.purchaseBillNumber ||
            "",
    },

    {
        label:
            "Supplier",

        value: (
            item
        ) =>
            item
                ?.supplier
                ?.supplierName ||
            "",
    },

    {
        label:
            "Reason",

        value: (
            item
        ) =>
            formatText(
                item
                    ?.reason
            ),
    },

    {
        label:
            "Quantity",

        value: (
            item
        ) =>
            toNumber(
                item
                    ?.totalQuantity
            ),
    },

    {
        label:
            "Taxable",

        value: (
            item
        ) =>
            formatNumber(
                item
                    ?.taxableAmount
            ),
    },

    {
        label:
            "Tax",

        value: (
            item
        ) =>
            formatNumber(
                item
                    ?.taxAmount
            ),
    },

    {
        label:
            "Debit Amount",

        value: (
            item
        ) =>
            formatNumber(
                item
                    ?.debitAmount
            ),
    },

    {
        label:
            "Status",

        value: (
            item
        ) =>
            formatText(
                item
                    ?.status
            ),
    },
];


/*
|--------------------------------------------------------------------------
| PAYMENT OUT
|--------------------------------------------------------------------------
*/

const paymentOutColumns = [
    {
        label:
            "Payment Number",

        value: (
            item
        ) =>
            item
                ?.paymentNumber ||
            "",
    },

    {
        label:
            "Payment Date",

        value: (
            item
        ) =>
            formatDate(
                item
                    ?.paymentDate
            ),
    },

    {
        label:
            "Supplier",

        value: (
            item
        ) =>
            item
                ?.supplier
                ?.supplierName ||
            "",
    },

    {
        label:
            "Payment Method",

        value: (
            item
        ) =>
            formatText(
                item
                    ?.paymentMethod
            ),
    },

    {
        label:
            "Reference",

        value: (
            item
        ) =>
            item
                ?.referenceNumber ||
            "",
    },

    {
        label:
            "Amount",

        value: (
            item
        ) =>
            formatNumber(
                item
                    ?.amount
            ),
    },

    {
        label:
            "Allocated",

        value: (
            item
        ) =>
            formatNumber(
                item
                    ?.allocatedAmount
            ),
    },

    {
        label:
            "Advance",

        value: (
            item
        ) =>
            formatNumber(
                item
                    ?.advanceAmount
            ),
    },

    {
        label:
            "Status",

        value: (
            item
        ) =>
            formatText(
                item
                    ?.status
            ),
    },
];


/*
|--------------------------------------------------------------------------
| OUTSTANDING
|--------------------------------------------------------------------------
*/

const outstandingColumns = [
    {
        label:
            "Purchase Bill",

        value: (
            item
        ) =>
            item
                ?.documentNumber ||
            "",
    },

    {
        label:
            "Supplier",

        value: (
            item
        ) =>
            item
                ?.supplier
                ?.supplierName ||
            "",
    },

    {
        label:
            "Bill Date",

        value: (
            item
        ) =>
            formatDate(
                item
                    ?.documentDate
            ),
    },

    {
        label:
            "Due Date",

        value: (
            item
        ) =>
            formatDate(
                item
                    ?.dueDate
            ),
    },

    {
        label:
            "Bill Total",

        value: (
            item
        ) =>
            formatNumber(
                item
                    ?.grandTotal
            ),
    },

    {
        label:
            "Debit Note",

        value: (
            item
        ) =>
            formatNumber(
                item
                    ?.debitNoteAmount
            ),
    },

    {
        label:
            "Paid",

        value: (
            item
        ) =>
            formatNumber(
                item
                    ?.paidAmount
            ),
    },

    {
        label:
            "Outstanding",

        value: (
            item
        ) =>
            formatNumber(
                item
                    ?.outstandingAmount
            ),
    },

    {
        label:
            "Days Overdue",

        value: (
            item
        ) =>
            toNumber(
                item
                    ?.daysOverdue
            ),
    },
];


/*
|--------------------------------------------------------------------------
| AGEING
|--------------------------------------------------------------------------
*/

const ageingColumns = [
    {
        label:
            "Supplier",

        value: (
            item
        ) =>
            item
                ?.supplier
                ?.supplierName ||
            "",
    },

    {
        label:
            "Not Due",

        value: (
            item
        ) =>
            formatNumber(
                item
                    ?.notDue
            ),
    },

    {
        label:
            "0-30 Days",

        value: (
            item
        ) =>
            formatNumber(
                item
                    ?.days0To30
            ),
    },

    {
        label:
            "31-60 Days",

        value: (
            item
        ) =>
            formatNumber(
                item
                    ?.days31To60
            ),
    },

    {
        label:
            "61-90 Days",

        value: (
            item
        ) =>
            formatNumber(
                item
                    ?.days61To90
            ),
    },

    {
        label:
            "90+ Days",

        value: (
            item
        ) =>
            formatNumber(
                item
                    ?.days90Plus
            ),
    },

    {
        label:
            "Outstanding",

        value: (
            item
        ) =>
            formatNumber(
                item
                    ?.totalOutstanding
            ),
    },
];


/*
|--------------------------------------------------------------------------
| GST INPUT
|--------------------------------------------------------------------------
*/

const gstInputColumns = [
    {
        label:
            "Purchase Bill",

        value: (
            item
        ) =>
            item
                ?.documentNumber ||
            "",
    },

    {
        label:
            "Bill Date",

        value: (
            item
        ) =>
            formatDate(
                item
                    ?.documentDate
            ),
    },

    {
        label:
            "Supplier",

        value: (
            item
        ) =>
            item
                ?.supplier
                ?.supplierName ||
            "",
    },

    {
        label:
            "GSTIN",

        value: (
            item
        ) =>
            item
                ?.supplier
                ?.gstin ||
            "",
    },

    {
        label:
            "Taxable",

        value: (
            item
        ) =>
            formatNumber(
                item
                    ?.taxableAmount
            ),
    },

    {
        label:
            "CGST",

        value: (
            item
        ) =>
            formatNumber(
                item
                    ?.cgstAmount
            ),
    },

    {
        label:
            "SGST",

        value: (
            item
        ) =>
            formatNumber(
                item
                    ?.sgstAmount
            ),
    },

    {
        label:
            "IGST",

        value: (
            item
        ) =>
            formatNumber(
                item
                    ?.igstAmount
            ),
    },

    {
        label:
            "Cess",

        value: (
            item
        ) =>
            formatNumber(
                item
                    ?.cessAmount
            ),
    },

    {
        label:
            "Grand Total",

        value: (
            item
        ) =>
            formatNumber(
                item
                    ?.grandTotal
            ),
    },
];


/*
|--------------------------------------------------------------------------
| REPORT CONFIG
|--------------------------------------------------------------------------
*/

const REPORT_CONFIG = {
    PURCHASE_REGISTER: {
        filename:
            "purchase-register.csv",

        columns:
            purchaseRegisterColumns,
    },

    SUPPLIER_SUMMARY: {
        filename:
            "supplier-purchase-summary.csv",

        columns:
            supplierSummaryColumns,
    },

    DEBIT_NOTES: {
        filename:
            "purchase-debit-notes.csv",

        columns:
            debitNoteColumns,
    },

    PAYMENT_OUT: {
        filename:
            "payment-out-report.csv",

        columns:
            paymentOutColumns,
    },

    OUTSTANDING: {
        filename:
            "purchase-outstanding.csv",

        columns:
            outstandingColumns,
    },

    AGEING: {
        filename:
            "supplier-ageing.csv",

        columns:
            ageingColumns,
    },

    GST_INPUT: {
        filename:
            "gst-input-report.csv",

        columns:
            gstInputColumns,
    },
};


/*
|--------------------------------------------------------------------------
| EXPORT PURCHASE REPORT
|--------------------------------------------------------------------------
*/

export const exportPurchaseReport = ({
    reportType,
    records = [],
}) => {
    const config =
        REPORT_CONFIG[
        reportType
        ];

    if (
        !config
    ) {
        throw new Error(
            "Unsupported purchase report."
        );
    }

    if (
        !Array.isArray(
            records
        ) ||
        records.length ===
        0
    ) {
        throw new Error(
            "No records available to export."
        );
    }

    const csv =
        generateCsv(
            config.columns,
            records
        );

    downloadCsv(
        config.filename,
        csv
    );
};